import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { IUser } from '@shared/models/iuser.model';
import { SharedService } from '@shared/services/shared.service';
import { debounceTime, filter, switchMap } from 'rxjs';
import { ContactListItem, SelectionEvent } from './contact-list/contact-list.model';
import { EntityType } from '@shared/models/entity-enum';

export interface ShareEvent {
  selectedUsers: IUser[];
  notes: string;
}

@UntilDestroy()
@Component({
  selector: 'miq-share-with-contacts',
  templateUrl: './share-with-contacts.component.html',
  styleUrls: ['./share-with-contacts.component.scss'],
  standalone: false,
})
export class ShareWithContactsComponent implements OnInit {
  @Input() notesLabel = '';
  @Input() notesPlaceHolder = '';
  @Input() leaseIds: number[] = [];
  @Input() totalLeaseCount = 0;
  @Input() entity: string;
  @Output() share = new EventEmitter<ShareEvent>();
  public isLeaseComp = false;

  public contactList = [];
  public sharedFormGroup: UntypedFormGroup;
  public selectedUsers: IUser[] = [];

  constructor(private sharedService: SharedService) {}

  ngOnInit(): void {
    this.isLeaseComp = this.entity === EntityType.LeaseComp;
    this.sharedFormGroup = new UntypedFormGroup({
      notes: new UntypedFormControl(''),
      searchName: new UntypedFormControl(''),
      lcplShareSearchCheck: new UntypedFormControl(false),
    });

    this.searchControl.valueChanges
      .pipe(
        untilDestroyed(this),
        debounceTime(400),
        filter(searchText => !!searchText),
        switchMap((searchText: string) => this.searchForContact(searchText))
      )
      .subscribe((listOfContacts: IUser[]) => {
        this.contactList = this.getUserlist(this.selectedUsers, listOfContacts);
      });
  }

  get searchControl() {
    return this.sharedFormGroup.get('searchName');
  }

  get notesControl() {
    return this.sharedFormGroup.get('notes');
  }

  get lcplShareSearchCheckControl() {
    return this.sharedFormGroup.get('lcplShareSearchCheck');
  }

  public onSelectionChange(evt: SelectionEvent) {
    this.selectedUsers = evt.checked
      ? [...this.selectedUsers, evt.user]
      : this.removeSelectedUser(this.selectedUsers, evt.user);
  }

  public onShare() {
    if (!this.selectedUsers.length) return;
    this.share.emit({ selectedUsers: this.selectedUsers, notes: this.notesControl.value as string });
  }

  private searchForContact(searchText: string) {
    return this.sharedService.searchMiqUser(searchText.toLowerCase()).pipe();
  }

  /**
   * Merge the selected items by the user with the items from the search request
   * @param baseList
   * @param itemsToAdd
   * @returns
   */
  private getUserlist(baseList: IUser[], itemsToAdd: IUser[]): ContactListItem[] {
    return [...baseList, ...itemsToAdd.filter(itemToAdd => !this.isContactInTheList(baseList, itemToAdd))]
      .sort((a, b) => (a.emailAddress > b.emailAddress ? 1 : -1))
      .map(itemToFormat => this.getContactFormatted(baseList, itemToFormat));
  }

  /**
   * Validates if a contact is already in the list by comparing emails
   * @param list
   * @param itemToFind
   * @returns true/false
   */
  private isContactInTheList(list: IUser[], itemToFind: IUser): boolean {
    return list.some(
      contact =>
        contact.emailAddress != null &&
        itemToFind.emailAddress != null &&
        contact.emailAddress.toLowerCase() === itemToFind.emailAddress.toLowerCase()
    );
  }

  /**
   * Returns a contact with the required format for the list component
   * @param baseList
   * @param contact
   * @returns ContactListItem
   */
  private getContactFormatted(baseList: IUser[], contact: IUser): ContactListItem {
    return {
      user: contact,
      isSelected: this.isContactInTheList(baseList, contact),
      isOnMIQ: true,
    };
  }

  /**
   * Removes a contact from a list
   * @param list
   * @param itemToRemove
   * @returns IUser[]
   */
  private removeSelectedUser(list: IUser[], itemToRemove: IUser): IUser[] {
    return list.filter(contact => contact.id !== itemToRemove.id);
  }

  navigateToLease(leaseId: number) {
    window.open(`/records/lease/${leaseId}/edit`, '_blank');
  }
}
